import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';

import useInView from '@nubank/www-latam-commons/utils/hooks/useInView';
import { AnnouncementContext } from 'components/AnnouncementBar/AnnouncementBarProvider';

import QuickNavigationContext from './QuickNavigationContext';
import Navigation from './components/Navigation/Navigation';

const elementVisibilityPercentualInWindow = 0.5;

const initialContext = () => {
  const [items, setItems] = useState({});
  const [selected, setSelected] = useState();
  const addItem = ({ id, intlKey }) => {
    items[id] = intlKey;
    setItems(items);
  };

  const removeItem = id => {
    delete items[id];
    setItems(items);
  };

  return {
    items,
    addItem,
    removeItem,
    selected,
    setSelected,
  };
};

function QuickNavigationItem({
  id,
  intlKey,
  children,
}) {
  const context = useContext(QuickNavigationContext);
  const sectionRef = useRef();
  const threshold = useMemo(() => {
    const { current } = sectionRef;
    return current
      ? ((window.innerHeight * elementVisibilityPercentualInWindow) / current.offsetHeight)
      : elementVisibilityPercentualInWindow;
  }, [sectionRef.current]);
  const [inViewRef, isVisible] = useInView({
    intersectionOptions: {
      triggerOnce: false,
      threshold: threshold >= 1 ? 1 : threshold,
      rootMargin: '70px',
    },
  });

  const setRefs = useCallback(node => {
    sectionRef.current = node;
    inViewRef(node);
  }, [inViewRef]);

  useEffect(() => {
    context.addItem({ id, intlKey });
    return () => {
      context.removeItem(id);
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      context.setSelected(id);
    }
  }, [isVisible]);

  return (
    <div id={`navigation-item-${id}`} ref={setRefs}>
      {children}
    </div>
  );
}

QuickNavigationItem.propTypes = {
  children: PropTypes.node.isRequired,
  id: PropTypes.string.isRequired,
  intlKey: PropTypes.string.isRequired,
};

function QuickNavigation({ children, isAnchorAllowed, onClick }) {
  const { isVisible: isAnnouncementVisible } = useContext(AnnouncementContext);
  const [navigationVisible, setNavigationVisibility] = useState(false);
  const sentinelConfig = {
    intersectionOptions: {
      triggerOnce: false,
      threshold: 0.01,
      initialInView: true,
    },
  };
  const [startSentinelRef, isStartVisible] = useInView(sentinelConfig);

  useEffect(() => {
    setNavigationVisibility(!isStartVisible);
  }, [isStartVisible]);

  return (
    <QuickNavigationContext.Provider value={initialContext()}>
      <span ref={startSentinelRef} />
      {children}
      {(!isAnnouncementVisible || isAnchorAllowed) && (
        <Navigation isVisible={navigationVisible} onClick={onClick} />
      )}
    </QuickNavigationContext.Provider>
  );
}

QuickNavigation.defaultProps = {
  onClick: undefined,
};

QuickNavigation.propTypes = {
  children: PropTypes.node.isRequired,
  isAnchorAllowed: PropTypes.bool.isRequired,
  onClick: PropTypes.func,
};

QuickNavigation.Item = QuickNavigationItem;

export default QuickNavigation;
